import { portalAPI } from './portalAPI';

import type { FirmWidget, GalleryWidget, WidgetRecipients, WidgetSharingSave } from 'types/homeWidgetSharing';

/**
 * Widgets shared with the firm. Both lists carry the `HomeWidgets` tag, so they are read again when a widget is
 * built, changed or deleted as well as when one is shared.
 */
export const APIHomeWidgetSharingSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Home', 'HomeWidgets'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            /** What the firm published for everyone to add to their Home. */
            getWidgetGallery: builder.query<GalleryWidget[], void>({
                query: () => '/v1/home-widgets/gallery',
                providesTags: ['HomeWidgets'],
            }),
            /** Every shared widget and whom it is shared with: the only read of a widget's recipients. */
            getFirmWidgets: builder.query<FirmWidget[], void>({
                query: () => '/v1/home-widgets/firm',
                providesTags: ['HomeWidgets'],
            }),
            /** How many people a choice of recipients reaches today. Nothing is stored, so it is a query. */
            countRecipients: builder.query<number, WidgetRecipients>({
                query: (body) => ({ url: '/v1/home-widgets/recipients/count', method: 'POST', body }),
                transformResponse: (answer: { count: number }) => answer.count,
                // A read sent as a POST: it must not hold, nor release, the lock a write puts on the submit buttons.
                extraOptions: { skipButtonDisable: true },
            }),
            /** The answer is empty, and the sharer's own Home can gain or lose the widget: both are read again. */
            setWidgetSharing: builder.mutation<void, WidgetSharingSave & { id: number }>({
                query: ({ id, ...body }) => ({ url: `/v1/home-widgets/${id}/sharing`, method: 'PUT', body }),
                invalidatesTags: ['Home', 'HomeWidgets'],
            }),
        }),
    });

export const {
    useGetWidgetGalleryQuery,
    useGetFirmWidgetsQuery,
    useCountRecipientsQuery,
    useSetWidgetSharingMutation,
} = APIHomeWidgetSharingSlice;
